import React, { useState } from 'react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './components/ui/select';
import { 
  History as HistoryIcon, 
  Search, 
  Filter,
  Clock,
  User,
  ChevronLeft,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { mockHistory } from './components/data/mockData';

const ITEMS_PER_PAGE = 20;

const actionLabels = {
  created: 'Criado',
  updated: 'Atualizado',
  deleted: 'Excluído',
  status_changed: 'Status alterado',
  assignee_changed: 'Responsável alterado'
};

const actionColors = {
  created: 'bg-emerald-100 text-emerald-700',
  updated: 'bg-blue-100 text-blue-700',
  deleted: 'bg-red-100 text-red-700',
  status_changed: 'bg-amber-100 text-amber-700',
  assignee_changed: 'bg-purple-100 text-purple-700'
};

export default function History() {
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading] = useState(false);

  const allHistory = mockHistory;

  // Get unique users from history
  const uniqueUsers = [...new Set(allHistory.map(h => h.changed_by).filter(Boolean))];

  // Apply filters
  const filteredHistory = allHistory.filter(item => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        item.task_title?.toLowerCase().includes(query) ||
        item.field_changed?.toLowerCase().includes(query) ||
        item.old_value?.toLowerCase().includes(query) ||
        item.new_value?.toLowerCase().includes(query) ||
        item.changed_by?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Action filter
    if (actionFilter !== 'all' && item.action !== actionFilter) return false;

    // User filter
    if (userFilter !== 'all' && item.changed_by !== userFilter) return false;

    // Date filters
    if (dateFrom && item.created_date) {
      const itemDate = new Date(item.created_date);
      const fromDate = new Date(dateFrom);
      if (itemDate < fromDate) return false;
    }
    if (dateTo && item.created_date) {
      const itemDate = new Date(item.created_date);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (itemDate > toDate) return false;
    }

    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredHistory.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedHistory = filteredHistory.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, actionFilter, userFilter, dateFrom, dateTo]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-cyan-50/30">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200/50 sticky top-0 z-10 shadow-sm">
        <div className="px-4 md:px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-200 ring-4 ring-indigo-100">
                <HistoryIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                  Histórico de Atividades
                </h1>
                <p className="text-sm text-slate-600 font-medium">
                  {filteredHistory.length} {filteredHistory.length === 1 ? 'registro' : 'registros'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="p-4 md:p-6">
        <div className="bg-white rounded-2xl shadow-sm border-2 border-slate-100 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-slate-600" />
            <h2 className="font-bold text-slate-800">Filtros</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar no histórico..."
                  className="pl-9"
                />
              </div>
            </div>

            {/* Action Filter */}
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Ação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as ações</SelectItem>
                {Object.entries(actionLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* User Filter */}
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Usuário" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os usuários</SelectItem>
                {uniqueUsers.map((user) => (
                  <SelectItem key={user} value={user}>{user}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date Filters */}
            <div className="flex gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="De"
                className="flex-1"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="Até"
                className="flex-1"
              />
            </div>
          </div>
        </div>

        {/* History List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        ) : paginatedHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400 bg-white rounded-2xl border-2 border-slate-100">
            <HistoryIcon className="w-16 h-16 mb-3" />
            <p className="text-lg font-medium">Nenhum registro encontrado</p>
            <p className="text-sm">Tente ajustar os filtros</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {paginatedHistory.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl p-4 border-2 border-slate-100 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 pt-1">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center shadow-md">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge className={`${actionColors[item.action] || 'bg-slate-100 text-slate-700'} font-semibold shadow-sm`}>
                          {actionLabels[item.action] || item.action}
                        </Badge>
                        {item.task_title && (
                          <span className="text-sm font-medium text-slate-700">
                            "{item.task_title}"
                          </span>
                        )}
                        {item.field_changed && (
                          <span className="text-xs text-slate-500">
                            • Campo: {item.field_changed}
                          </span>
                        )}
                      </div>
                      
                      {(item.old_value || item.new_value) && (
                        <div className="text-sm mb-2 space-y-1 bg-slate-50 p-3 rounded-lg">
                          {item.old_value && (
                            <div className="text-slate-600">
                              <span className="font-semibold text-slate-700">De:</span> {item.old_value}
                            </div>
                          )}
                          {item.new_value && (
                            <div className="text-slate-600">
                              <span className="font-semibold text-slate-700">Para:</span> {item.new_value}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        {item.changed_by && (
                          <span className="flex items-center gap-1 font-medium">
                            <User className="w-3 h-3" />
                            {item.changed_by}
                          </span>
                        )}
                        {item.created_date && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {format(new Date(item.created_date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 bg-white rounded-xl p-4 border-2 border-slate-100">
                <div className="text-sm text-slate-600">
                  Página {currentPage} de {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}